import { useEffect, useRef, useState } from 'react';
import { S } from '../strings';

// 'yhteiso' is the community (independent-blogger) tab; the other three are
// the official views.
export type ViewMode = 'kategoriat' | 'priorisoitu' | 'roadmap' | 'yhteiso';

export interface DashboardHeaderProps {
  displayName: string | null;
  signOut: () => void;
  isLive: boolean;
  loading: boolean;
  lastRefresh: Date | null;
  dataCount: number;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  viewMode: ViewMode;
  setViewMode: (v: ViewMode) => void;
  onRefresh: () => void;
  // "Näytä kaikki": include muted items (dimmed) in every view. The toggle
  // only renders when the shell wires onToggleShowMuted.
  showMuted?: boolean;
  mutedCount?: number;
  onToggleShowMuted?: () => void;
  // Low-key "Tietolähteet" link under the title; renders only when wired.
  onOpenDataSources?: () => void;
  // Subtle warning dot next to that link: an enabled feed's last run was not
  // OK, or an enabled feed is stale (no new items for STALE_AFTER_DAYS).
  dataSourcesWarning?: boolean;
}

function firstNameFrom(displayName: string): string {
  const local = displayName.includes('@') ? displayName.split('@')[0] : displayName;
  const first = local.split(/[\s.]+/)[0] ?? '';
  return first ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase() : '';
}

// The gradient bar at the top of the dashboard: app title + live status,
// optional greeting, search input, view-mode toggle, manual refresh, and the
// user-menu dropdown. Owns its own user-menu open/close state and the
// click-outside handler — nothing else consumes them.
export function DashboardHeader({
  displayName,
  signOut,
  isLive,
  loading,
  lastRefresh,
  dataCount,
  searchQuery,
  setSearchQuery,
  viewMode,
  setViewMode,
  onRefresh,
  showMuted = false,
  mutedCount = 0,
  onToggleShowMuted,
  onOpenDataSources,
  dataSourcesWarning = false,
}: DashboardHeaderProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Close the user menu on any click outside it.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!userMenuRef.current?.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [userMenuOpen]);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #0078D4 0%, #005A9E 50%, #003C71 100%)',
        padding: '18px 28px',
      }}
    >
      <div
        className="header-content"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            style={{
              width: 40,
              height: 40,
              background: 'rgba(255,255,255,0.12)',
              borderRadius: 11,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <div>
            <div style={{ color: '#fff', fontSize: 20, fontWeight: 800, letterSpacing: -0.5 }}>
              {S.appTitle}
            </div>
            <div
              style={{
                color: 'rgba(255,255,255,0.65)',
                fontSize: 12,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {isLive ? (
                <>
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: '#4ADE80',
                      display: 'inline-block',
                    }}
                  />
                  {S.liveLabel} — {dataCount} päivitystä — {lastRefresh?.toLocaleTimeString('fi-FI')}
                </>
              ) : loading ? (
                <>
                  <span className="pulse">●</span> {S.loading}
                </>
              ) : (
                S.appSubtitle
              )}
              {onOpenDataSources && (
                <button
                  type="button"
                  onClick={onOpenDataSources}
                  aria-haspopup="dialog"
                  style={{
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    color: 'rgba(255,255,255,0.75)',
                    fontSize: 11,
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontFamily: 'inherit',
                  }}
                >
                  <span aria-hidden="true">ⓘ </span>
                  {S.dataSources.link}
                </button>
              )}
              {onOpenDataSources && dataSourcesWarning && (
                // Sibling of the link so the button's accessible name stays
                // "Tietolähteet"; the negative margin offsets the row gap.
                <span
                  role="img"
                  aria-label={S.dataSources.warning}
                  title={S.dataSources.warning}
                  style={{
                    width: 6,
                    height: 6,
                    marginLeft: -5,
                    borderRadius: '50%',
                    background: '#FBBF24',
                    // White ring keeps >= 3:1 contrast against the blue
                    // header gradient, which the amber fill alone does not.
                    boxShadow: '0 0 0 1.5px #fff',
                    display: 'inline-block',
                  }}
                />
              )}
            </div>
          </div>
          {displayName && firstNameFrom(displayName) && (
            <div
              style={{
                color: 'rgba(255,255,255,0.95)',
                fontSize: 20,
                fontWeight: 700,
                paddingLeft: 14,
                borderLeft: '1px solid rgba(255,255,255,0.25)',
                whiteSpace: 'nowrap',
              }}
            >
              {S.greeting} {firstNameFrom(displayName)}!
            </div>
          )}
        </div>
        <div
          className="header-actions"
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={S.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 9,
                padding: '9px 14px 9px 38px',
                color: '#fff',
                fontSize: 13,
                width: 220,
              }}
            />
            <svg
              style={{ position: 'absolute', left: 12, top: 10 }}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(255,255,255,0.5)"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </div>

          <div
            style={{
              background: 'rgba(255,255,255,0.12)',
              borderRadius: 9,
              padding: '4px 3px',
              display: 'flex',
            }}
          >
            {(['kategoriat', 'priorisoitu', 'roadmap', 'yhteiso'] as ViewMode[]).map((v) => (
              <button
                key={v}
                onClick={() => setViewMode(v)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 7,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  background: viewMode === v ? '#fff' : 'transparent',
                  color: viewMode === v ? '#0078D4' : 'rgba(255,255,255,0.75)',
                  transition: 'all 0.2s',
                }}
              >
                {v === 'kategoriat'
                  ? S.viewCategories
                  : v === 'priorisoitu'
                    ? S.viewPriority
                    : v === 'roadmap'
                      ? S.viewRoadmap
                      : S.viewCommunity}
              </button>
            ))}
          </div>

          {onToggleShowMuted && (
            <button
              type="button"
              onClick={onToggleShowMuted}
              aria-pressed={showMuted}
              title={S.mute.showAllTitle(mutedCount)}
              style={{
                padding: '8px 12px',
                borderRadius: 9,
                border: 'none',
                background: showMuted ? '#fff' : 'rgba(255,255,255,0.15)',
                color: showMuted ? '#0078D4' : 'rgba(255,255,255,0.85)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
              }}
            >
              {S.mute.showAll}
            </button>
          )}

          {isLive && (
            <button
              onClick={onRefresh}
              disabled={loading}
              style={{
                padding: '8px 14px',
                borderRadius: 9,
                border: 'none',
                background: 'rgba(255,255,255,0.15)',
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {loading ? '⏳' : '🔄'}
            </button>
          )}

          {displayName && (
            <div ref={userMenuRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
                style={{
                  padding: '6px 10px 6px 6px',
                  borderRadius: 9,
                  border: 'none',
                  background: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.25)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                >
                  {displayName[0]?.toUpperCase() ?? '?'}
                </span>
                <span
                  style={{
                    maxWidth: 140,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayName}
                </span>
                <span style={{ fontSize: 9, opacity: 0.7 }}>{userMenuOpen ? '▲' : '▼'}</span>
              </button>
              {userMenuOpen && (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 6px)',
                    background: '#fff',
                    borderRadius: 9,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
                    minWidth: 180,
                    padding: 6,
                    zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      padding: '8px 12px 4px',
                      fontSize: 11,
                      color: 'var(--text-muted)',
                      borderBottom: '1px solid var(--border)',
                      marginBottom: 4,
                    }}
                  >
                    {displayName}
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setUserMenuOpen(false);
                      signOut();
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: 'none',
                      background: 'transparent',
                      color: '#D13438',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#FEF2F2')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    Kirjaudu ulos
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
